// ─── Types ───────────────────────────────────────────────────────────────────

export type SubOption = {
  label: string
  phone: string
  ringCount: number
  noAnswer: `forward` | `voicemail`
  forwardTo: string
}

export type MenuOption = {
  label: string
  subGreeting: string
  subAudioType: `tts` | `s3`
  subAudioKey: string
  subOptions: SubOption[]
}

export type IvrFormState = {
  enabled: boolean
  businessHoursEnabled: boolean
  afterHoursMessage: string
  afterHoursAudioType: `tts` | `s3`
  afterHoursAudioKey: string
  greeting: string
  audioType: `tts` | `s3`
  audioKey: string
  menuOptions: MenuOption[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const MAX_GREETING_LENGTH = 500
export const MAX_MENU_OPTIONS = 9
export const MAX_SUB_OPTIONS = 9
export const DEFAULT_RING_COUNT = 5

export const AUDIO_TYPE_OPTIONS = [
  { value: `tts`, label: `Text-to-Speech` },
  { value: `s3`, label: `Upload Audio File` },
]

export const NO_ANSWER_OPTIONS = [
  { id: `voicemail`, name: `Voicemail`, value: `voicemail` },
  { id: `forward`, name: `Forward to...`, value: `forward` },
]

// ─── Defaults ────────────────────────────────────────────────────────────────

export const defaultSubOption = (): SubOption => ({
  label: ``,
  phone: ``,
  ringCount: DEFAULT_RING_COUNT,
  noAnswer: `voicemail`,
  forwardTo: ``,
})

export const defaultMenuOption = (): MenuOption => ({
  label: ``,
  subGreeting: ``,
  subAudioType: `tts`,
  subAudioKey: ``,
  subOptions: [defaultSubOption()],
})

// ─── Config ↔ Form Converters ────────────────────────────────────────────────

/** Parse an IvrConfig into the structured form state */
export const parseConfigToForm = (config: any): IvrFormState => {
  const defaultState: IvrFormState = {
    enabled: true,
    businessHoursEnabled: false,
    afterHoursMessage: ``,
    afterHoursAudioType: `tts`,
    afterHoursAudioKey: ``,
    greeting: ``,
    audioKey: ``,
    audioType: `tts`,
    menuOptions: [],
  }

  if (!config?.root || !config?.nodes) return defaultState

  let effectiveRoot = config.root
  let businessHoursEnabled = false
  let afterHoursMessage = ``
  let afterHoursAudioType: `tts` | `s3` = `tts`
  let afterHoursAudioKey = ``

  // If root is a businessHours node, extract its config and skip to duringHours
  const rootNode = config.nodes[config.root]
  if (rootNode?.type === `businessHours`) {
    businessHoursEnabled = true
    effectiveRoot = rootNode.duringHours || config.root
    const afterNode = rootNode.afterHours ? config.nodes[rootNode.afterHours] : undefined
    if (afterNode?.type === `announcement` || afterNode?.type === `voicemail`) {
      const audio = afterNode.audio
      afterHoursMessage = audio?.text || ``
      afterHoursAudioType = audio?.type || `tts`
      afterHoursAudioKey = audio?.type === `s3` ? audio?.key || `` : ``
    }
  }

  const greetingNode = config.nodes[effectiveRoot]
  const menuNodeId = greetingNode?.type === `greeting` ? greetingNode.next : effectiveRoot
  const menuNode = config.nodes[menuNodeId]

  // Audio lives on the menu node (inside Gather), not on the greeting passthrough
  const audioSource = menuNode?.audio || greetingNode?.audio
  const greeting = audioSource?.text || ``
  const audioKey = audioSource?.type === `s3` ? (audioSource as any)?.key || `` : ``
  const audioType = audioSource?.type || `tts`

  const menuOptions: MenuOption[] = []
  if (menuNode?.type === `menu` && menuNode.options) {
    for (const { targetNodeId } of menuNode.options) {
      const targetNode = config.nodes[targetNodeId]
      if (!targetNode) {
        menuOptions.push(defaultMenuOption())
        continue
      }

      if (targetNode.type === `menu`) {
        const subOptions: SubOption[] = []
        for (const { targetNodeId: subTargetId } of targetNode.options || []) {
          const subNode = config.nodes[subTargetId]
          if (subNode?.type === `dial`) {
            subOptions.push({
              label: (subNode as any)?.label || ``,
              phone: subNode.target?.startsWith(`$`) ? `` : subNode.target || ``,
              ringCount: subNode.ringCount ?? DEFAULT_RING_COUNT,
              noAnswer: subNode.noAnswer && config.nodes[subNode.noAnswer]?.type === `dial` ? `forward` : `voicemail`,
              forwardTo:
                subNode.noAnswer && config.nodes[subNode.noAnswer]?.type === `dial`
                  ? config.nodes[subNode.noAnswer]?.target || ``
                  : ``,
            })
          }
        }
        menuOptions.push({
          label: (targetNode as any)?.label || ``,
          subGreeting: targetNode.audio?.text || ``,
          subAudioType: targetNode.audio?.type || `tts`,
          subAudioKey: targetNode.audio?.type === `s3` ? (targetNode.audio as any)?.key || `` : ``,
          subOptions: subOptions.length ? subOptions : [defaultSubOption()],
        })
      } else if (targetNode.type === `dial`) {
        menuOptions.push({
          label: ``,
          subGreeting: ``,
          subAudioType: `tts`,
          subAudioKey: ``,
          subOptions: [
            {
              label: (targetNode as any)?.label || ``,
              phone: targetNode.target?.startsWith(`$`) ? `` : targetNode.target || ``,
              ringCount: targetNode.ringCount ?? DEFAULT_RING_COUNT,
              noAnswer:
                targetNode.noAnswer && config.nodes[targetNode.noAnswer]?.type === `dial` ? `forward` : `voicemail`,
              forwardTo:
                targetNode.noAnswer && config.nodes[targetNode.noAnswer]?.type === `dial`
                  ? config.nodes[targetNode.noAnswer]?.target || ``
                  : ``,
            },
          ],
        })
      }
    }
  }

  return {
    enabled: config.enabled !== false,
    businessHoursEnabled,
    afterHoursMessage,
    afterHoursAudioType,
    afterHoursAudioKey,
    greeting,
    audioKey,
    audioType,
    menuOptions,
  }
}

/** Convert the structured form state back to an IvrConfig */
export const formToConfig = (form: IvrFormState): any => {
  const nodes: Record<string, any> = {}
  let nodeCounter = 0
  const nextId = () => `node-${++nodeCounter}`

  const greetingId = nextId()
  const menuId = nextId()

  const greetingAudio =
    form.audioType === `tts` ? { type: `tts`, text: form.greeting } : { type: `s3`, key: form.audioKey }

  nodes[greetingId] = { type: `greeting`, audio: { type: `tts`, text: `` }, next: menuId }

  const isSubOptionConfigured = (sub: SubOption) => sub.phone !== ``

  const menuOptions: { digit: string; targetNodeId: string }[] = []
  for (let i = 0; i < form.menuOptions.length; i++) {
    const opt = form.menuOptions[i]
    const digit = String(i + 1)
    const configuredSubs = opt.subOptions.filter(isSubOptionConfigured)

    // Skip menu options with no configured sub-options
    if (!configuredSubs.length) continue

    // Single sub-option with no sub-greeting → flat dial node
    if (configuredSubs.length === 1 && !opt.subGreeting) {
      const sub = configuredSubs[0]
      const dialId = nextId()

      let noAnswerId: string | undefined
      if (sub.noAnswer === `forward` && sub.forwardTo) {
        noAnswerId = nextId()
        nodes[noAnswerId] = { type: `dial`, target: sub.forwardTo, ringCount: DEFAULT_RING_COUNT }
      } else if (sub.noAnswer === `voicemail`) {
        noAnswerId = nextId()
        nodes[noAnswerId] = { type: `voicemail`, email: `` }
      }

      nodes[dialId] = {
        type: `dial`,
        label: sub.label,
        target: sub.phone,
        ringCount: sub.ringCount,
        ...(noAnswerId ? { noAnswer: noAnswerId } : {}),
      }
      menuOptions.push({ digit, targetNodeId: dialId })
    } else {
      // Multiple sub-options → nested sub-menu
      const subMenuId = nextId()
      const subMenuOptions: { digit: string; targetNodeId: string }[] = []

      for (let j = 0; j < configuredSubs.length; j++) {
        const sub = configuredSubs[j]
        const subDigit = String(j + 1)
        const dialId = nextId()

        let noAnswerId: string | undefined
        if (sub.noAnswer === `forward` && sub.forwardTo) {
          noAnswerId = nextId()
          nodes[noAnswerId] = { type: `dial`, target: sub.forwardTo, ringCount: DEFAULT_RING_COUNT }
        } else if (sub.noAnswer === `voicemail`) {
          noAnswerId = nextId()
          nodes[noAnswerId] = { type: `voicemail`, email: `` }
        }

        nodes[dialId] = {
          type: `dial`,
          label: sub.label,
          target: sub.phone,
          ringCount: sub.ringCount,
          ...(noAnswerId ? { noAnswer: noAnswerId } : {}),
        }
        subMenuOptions.push({ digit: subDigit, targetNodeId: dialId })
      }

      const subMenuAudio =
        opt.subAudioType === `tts` ? { type: `tts`, text: opt.subGreeting } : { type: `s3`, key: opt.subAudioKey }

      nodes[subMenuId] = {
        type: `menu`,
        label: opt.label,
        audio: subMenuAudio,
        options: subMenuOptions,
        timeout: 10,
        noInput: `replay`,
      }
      menuOptions.push({ digit, targetNodeId: subMenuId })
    }
  }

  nodes[menuId] = {
    type: `menu`,
    audio: greetingAudio,
    options: menuOptions,
    timeout: 10,
    noInput: `replay`,
  }

  // Wrap with businessHours node if enabled
  if (form.businessHoursEnabled) {
    const afterHoursId = nextId()
    const afterHoursAudio =
      form.afterHoursAudioType === `tts`
        ? { type: `tts`, text: form.afterHoursMessage }
        : { type: `s3`, key: form.afterHoursAudioKey }
    nodes[afterHoursId] = { type: `announcement`, audio: afterHoursAudio }

    const bhId = nextId()
    nodes[bhId] = { type: `businessHours`, duringHours: greetingId, afterHours: afterHoursId }
    return { root: bhId, nodes, enabled: form.enabled }
  }

  return { root: greetingId, nodes, enabled: form.enabled }
}
